/**
 * Core logic hook for the Schedule Interview wizard.
 * Wraps the generic useStepValidation hook with scheduling-specific
 * mutations, mode configuration, edit-mode hydration, and save logic.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';
import type { TabItem } from '@kula-ai/components';
import { useShowToast } from '@/hooks/useShowToast';
import {
  useCreateInterview,
  useUpdateInterview,
  useGetInterview,
} from '@/api/useInterviews';
import {
  useCreateSelfSchedule,
  useUpdateSelfSchedule,
} from '@/api/useSelfSchedule';
import { useStepValidation, type StepFormApi } from '@/hooks/useStepValidation';
import type {
  ScheduleMode,
  ApiReference,
  ScheduleInterviewPayload,
  ScheduleModeConfig,
} from './schedule.types';
import { MODE_CONFIGS } from './modes';
import { SCHEDULE_INTERVIEW_COPY } from './schedule.copy';
import { buildSchedulePayload, hydratePayloadFromApi } from './payload';
import { useInterviewPlan } from './api/useInterviewPlan';

interface UseScheduleInterviewOptions {
  mode: ScheduleMode;
  apiReference: ApiReference;
  applicationId?: number;
  candidateId?: number;
  jobId?: number;
  stageId?: number;
  stageActivityId?: number;
  interviewId?: number;
  scheduleId?: number;
  onClose?: () => void;
  onSuccess?: () => void;
}

export function useScheduleInterview({
  mode,
  apiReference,
  applicationId,
  candidateId,
  jobId,
  stageId,
  stageActivityId,
  interviewId,
  scheduleId,
  onClose,
  onSuccess,
}: UseScheduleInterviewOptions) {
  const toast = useShowToast();
  const config: ScheduleModeConfig = MODE_CONFIGS[mode];
  const isEdit = apiReference === 'edit';
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';

  // ---- Edit-mode GET ----
  const {
    data: existingInterview,
    isLoading: isLoadingExisting,
    isError: isLoadError,
    isFetched: isFetchedExisting,
  } = useGetInterview(interviewId, {
    enabled: isEdit && !!interviewId,
  });

  // ---- Payload ref + edit hydration ----
  const payloadRef = useRef<ScheduleInterviewPayload>({
    details: {},
    communication: {},
    scorecard: {},
  });

  const lastHydratedIdRef = useRef<number | null>(null);
  const prefillGenerationRef = useRef(0);

  if (!isEdit) {
    lastHydratedIdRef.current = null;
  }

  const editDataReady =
    isEdit &&
    existingInterview !== undefined &&
    isFetchedExisting &&
    !isLoadingExisting;

  if (editDataReady && existingInterview.id !== lastHydratedIdRef.current) {
    lastHydratedIdRef.current = existingInterview.id;
    prefillGenerationRef.current += 1;
    payloadRef.current = hydratePayloadFromApi(existingInterview);
  }

  const prefillVersion = isEdit ? prefillGenerationRef.current : 0;

  useEffect(() => {
    if (!isLoadError || !isEdit) return;
    toast({
      title: 'Could not load interview',
      description: 'Try closing and opening the editor again.',
      status: 'error',
    });
  }, [isEdit, isLoadError, toast]);

  // ---- Interview plan defaults (non-adhoc create) ----
  const isAdhoc = mode === 'adhoc' || mode === 'self-schedule-adhoc';
  const { data: interviewPlanFromNew } = useInterviewPlan(
    {
      stageId: stageId ?? 0,
      stageActivityId: stageActivityId ?? undefined,
      contactId: candidateId ?? undefined,
    },
    { enabled: !isAdhoc && !isEdit && !!stageId && stageId > 0 }
  );

  /** Create: `ats_interviews/new`. Edit: merge GET interview so shared-calendar + flags exist for Details schema. */
  const interviewPlan = useMemo((): Record<string, unknown> | undefined => {
    if (!isEdit || !existingInterview) {
      return interviewPlanFromNew;
    }
    const ex = existingInterview as Record<string, unknown>;
    const base = (interviewPlanFromNew ?? {}) as Record<string, unknown>;
    return {
      ...base,
      shared_calendar_enabled:
        base.shared_calendar_enabled ?? ex.shared_calendar_enabled,
      calendar_info: base.calendar_info ?? ex.calendar_info,
      scheduled_calendar: base.scheduled_calendar ?? ex.scheduled_calendar,
      meeting_rooms_enabled:
        base.meeting_rooms_enabled ?? ex.meeting_rooms_enabled,
    };
  }, [interviewPlanFromNew, isEdit, existingInterview]);

  // ---- Step refs ----
  const stepRefsMap = useRef<Record<string, RefObject<StepFormApi | null>>>({});

  const getStepRef = useCallback(
    (stepKey: string): RefObject<StepFormApi | null> => {
      if (!stepRefsMap.current[stepKey]) {
        stepRefsMap.current[stepKey] = { current: null };
      }
      return stepRefsMap.current[stepKey];
    },
    []
  );

  const stepRefs = useMemo(() => {
    const refs: Record<string, RefObject<StepFormApi | null>> = {};
    for (const step of config.steps) {
      refs[step.key] = getStepRef(step.key);
    }
    return refs;
  }, [config.steps, getStepRef]);

  // ---- Mutations ----
  const createInterview = useCreateInterview();
  const updateInterview = useUpdateInterview();
  const createSelfSchedule = useCreateSelfSchedule();
  const updateSelfSchedule = useUpdateSelfSchedule();

  const isSaving =
    createInterview.isPending ||
    updateInterview.isPending ||
    createSelfSchedule.isPending ||
    updateSelfSchedule.isPending;

  // ---- Save (onComplete callback for useStepValidation) ----
  const handleComplete = useCallback(
    async (payload: ScheduleInterviewPayload) => {
      const apiPayload = buildSchedulePayload(
        payload,
        {
          ats_application_id: applicationId,
          ats_job_id: jobId,
          ats_job_stage_id: stageId,
          ats_job_stage_action_id: stageActivityId ?? null,
          candidate_id: candidateId,
        },
        mode
      );

      try {
        if (isSelfSchedule) {
          if (isEdit && scheduleId) {
            await updateSelfSchedule.mutateAsync({
              scheduleId,
              payload: apiPayload,
            });
          } else {
            await createSelfSchedule.mutateAsync({
              payload: apiPayload,
            });
          }
        } else {
          if (isEdit && interviewId) {
            await updateInterview.mutateAsync({
              interviewId,
              payload: apiPayload,
            });
          } else {
            await createInterview.mutateAsync({
              payload: apiPayload,
            });
          }
        }

        toast({
          title: isEdit
            ? SCHEDULE_INTERVIEW_COPY.toast.updateSuccess.title
            : SCHEDULE_INTERVIEW_COPY.toast.createSuccess.title,
          status: 'success',
        });
        onSuccess?.();
        onClose?.();
      } catch {
        toast({
          title: SCHEDULE_INTERVIEW_COPY.toast.saveError.title,
          description: SCHEDULE_INTERVIEW_COPY.toast.saveError.fallback,
          status: 'error',
        });
      }
    },
    [
      mode,
      applicationId,
      candidateId,
      jobId,
      stageId,
      stageActivityId,
      createInterview,
      createSelfSchedule,
      interviewId,
      isEdit,
      isSelfSchedule,
      onClose,
      onSuccess,
      scheduleId,
      toast,
      updateInterview,
      updateSelfSchedule,
    ]
  );

  // ---- Build TabItems from mode config ----
  const tabItems = useMemo(
    (): TabItem[] =>
      config.steps.map((step) => ({
        label: step.label,
        value: step.key,
      })),
    [config.steps]
  );

  // ---- Step validation orchestration ----
  const finalActionLabel = isEdit
    ? SCHEDULE_INTERVIEW_COPY.footer.saveInterview
    : SCHEDULE_INTERVIEW_COPY.footer.scheduleInterview;

  const stepper = useStepValidation<ScheduleInterviewPayload>({
    tabItems,
    payloadRef,
    stepRefs,
    onComplete: handleComplete,
    finalActionLabel,
    nextPrefix: 'Next:',
    backPrefix: 'Back:',
    enableBack: true,
    wizardCompleteMode: 'validateAllSteps',
    activateFirstInvalidStepOnWizardComplete: true,
  });

  // ---- "Schedule Now" secondary CTA ----
  const scheduleNow = useCallback(() => {
    void stepper.goNextOrComplete();
  }, [stepper]);

  return {
    config,
    payloadRef,
    getStepRef,
    isSaving,
    scheduleNow,
    prefillVersion,
    interviewPlan,
    showEditLoader: isEdit && isLoadingExisting,
    showEditError: isEdit && isLoadError,
    // From useStepValidation
    tabItems: stepper.tabItems,
    activeStep: stepper.activeStep,
    isBusy: stepper.isBusy,
    onStepChange: stepper.onStepChange,
    goNextOrComplete: stepper.goNextOrComplete,
    goBack: stepper.goBack,
    canGoBack: stepper.canGoBack,
    primaryButtonLabel: stepper.primaryButtonLabel,
    backButtonLabel: stepper.backButtonLabel,
  };
}
